/**
 * CreditBalanceWidget — S28
 *
 * Displays the current user's own evaluation credit balance for a channel,
 * split into included (coach-allocated, monthly-reset) and purchased (top-up)
 * buckets.
 *
 * Degradation contract:
 *   - Renders null when loading, errored, or when S26 endpoint unavailable.
 *   - Never blocks submit or throws when hook returns undefined.
 *   - Shows own balance ONLY — never exposes another user's balance.
 */

import { useMyChannelCreditBalance } from '@/hooks/useCreditBalance';

interface CreditBalanceWidgetProps {
  /** Channel slug to fetch balance for. Widget renders null when falsy. */
  slug: string | null | undefined;
}

export function CreditBalanceWidget({ slug }: CreditBalanceWidgetProps) {
  const { data, isLoading, isError } = useMyChannelCreditBalance(slug);

  // Degrade gracefully: no render when loading, errored, or no data.
  if (!slug || isLoading || isError || !data) return null;

  // Hide entirely when the credit economy is not enforced (ledger flag OFF).
  // Showing "Included: 0 · Purchased: 0" when credits aren't a thing is
  // misleading and pairs with the (correctly suppressed) zero-balance gate.
  if (data.enforcement_active !== true) return null;

  const { included_available, purchased_available } = data;

  return (
    <div
      data-testid="credit-balance-widget"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '12px',
        padding: '8px 14px',
        borderRadius: '10px',
        backgroundColor: 'var(--color-dark-elevated-2)',
        border: '1px solid var(--color-border-subtle)',
        fontSize: '13px',
        color: 'var(--color-text-secondary)',
        flexWrap: 'wrap',
      }}
      aria-label="Your evaluation credit balance"
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
        {/* Credit coin icon */}
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          style={{ color: 'var(--color-accent)', flexShrink: 0 }}
        >
          <circle cx="12" cy="12" r="10" />
          <path d="M12 6v6l4 2" />
        </svg>
        <span style={{ color: 'var(--color-text-muted)', fontSize: '12px', fontWeight: 500 }}>
          Credits:
        </span>
      </span>

      <span
        data-testid="credit-balance-included"
        style={{ color: 'var(--color-text-primary)', fontWeight: 600 }}
      >
        Included:{' '}
        <span style={{ color: 'var(--color-accent)' }}>{included_available}</span>
        <span
          style={{
            color: 'var(--color-text-muted)',
            fontWeight: 400,
            fontSize: '11px',
            marginLeft: '3px',
          }}
        >
          (resets monthly)
        </span>
      </span>

      <span style={{ color: 'var(--color-border-strong)', userSelect: 'none' }}>·</span>

      <span
        data-testid="credit-balance-purchased"
        style={{ color: 'var(--color-text-primary)', fontWeight: 600 }}
      >
        Purchased:{' '}
        <span style={{ color: 'var(--color-accent-blue)' }}>{purchased_available}</span>
      </span>
    </div>
  );
}
